import React, { useEffect, useState } from "react";
import { Chart } from "primereact/chart";
import { handleGetRequest } from "../../services/GetTemplate";
import styles from "./PackageAnalytics.module.scss";
import { FiPackage, FiTruck, FiBarChart2, FiCalendar } from "react-icons/fi";

const PackageAnalytics = () => {
    const [monthlyData, setMonthlyData] = useState(null);
    const [statusData, setStatusData] = useState(null);
    const [deliveryData, setDeliveryData] = useState(null);
    const [weightData, setWeightData] = useState(null);
    const [specialHandling, setSpecialHandling] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Date range states
    const [dateRange, setDateRange] = useState("month");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

    const getCurrentDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const getDateRange = () => {
        const today = new Date();
        let startDate, endDate;

        switch (dateRange) {
            case "week":
                startDate = new Date(today);
                startDate.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
                endDate = new Date();
                break;
            case "month":
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date();
                break;
            case "year":
                startDate = new Date(today.getFullYear(), 0, 1);
                endDate = new Date();
                break;
            case "custom":
                startDate = customStartDate ? new Date(customStartDate) : new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = customEndDate ? new Date(customEndDate) : new Date();
                break;
            default:
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date();
        }

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        };
    };

    useEffect(() => {
        fetchPackageAnalytics();
    }, [dateRange, customStartDate, customEndDate]);

    const fetchPackageAnalytics = async () => {
        try {
            setLoading(true);
            setError(null);

            const { startDate, endDate } = getDateRange();

            const [monthly, status, delivery, weight, special] = await Promise.all([
                handleGetRequest(`/analytics/packages/monthly-trend?year=2025&startDate=${startDate}&endDate=${endDate}`).catch(e => {
                    console.error("Monthly trend error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/packages/status-distribution?startDate=${startDate}&endDate=${endDate}`).catch(e => {
                    console.error("Status distribution error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/packages/delivery-method?startDate=${startDate}&endDate=${endDate}`).catch(e => {
                    console.error("Delivery method error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/packages/weight-analytics?startDate=${startDate}&endDate=${endDate}`).catch(e => {
                    console.error("Weight analytics error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/packages/special-handling?startDate=${startDate}&endDate=${endDate}`).catch(e => {
                    console.error("Special handling error:", e);
                    return null;
                }),
            ]);

            // Process monthly trend
            if (monthly?.data) {
                setMonthlyData({
                    labels: monthly.data.map(item => item.monthName || item._id),
                    datasets: [
                        {
                            label: "Packages Shipped",
                            data: monthly.data.map(item => item.count),
                            fill: true,
                            borderColor: "#667eea",
                            backgroundColor: "rgba(102, 126, 234, 0.1)",
                            tension: 0.4,
                            borderWidth: 2,
                        },
                        {
                            label: "Total Weight (lbs)",
                            data: monthly.data.map(item => item.totalWeight),
                            fill: true,
                            borderColor: "#f59e0b",
                            backgroundColor: "rgba(245, 158, 11, 0.1)",
                            tension: 0.4,
                            borderWidth: 2,
                            yAxisID: "y1",
                        }
                    ]
                });
            }

            // Process status distribution with proper colors
            if (status?.data && status.data.length > 0) {
                const statusColors = {
                    'delivered': '#10b981',
                    'in_transit': '#3b82f6',
                    'pending': '#f59e0b',
                    'returned': '#ef4444',
                    'delayed': '#8b5cf6',
                    'lost': '#dc2626',
                    'cancelled': '#6b7280',
                };

                const colors = status.data.map(item => 
                    statusColors[item.status?.toLowerCase()?.replace(/\s+/g, '_')] || '#667eea'
                );

                setStatusData({
                    labels: status.data.map(item => item.status || 'Unknown'),
                    datasets: [
                        {
                            data: status.data.map(item => item.count),
                            backgroundColor: colors,
                            borderColor: "#fff",
                            borderWidth: 2,
                        }
                    ]
                });
            }

            // Process delivery method
            if (delivery?.data) {
                setDeliveryData({
                    labels: delivery.data.map(item => item.deliveryMethod),
                    datasets: [
                        {
                            label: "Packages",
                            data: delivery.data.map(item => item.count),
                            backgroundColor: ["#667eea", "#10b981", "#f59e0b"],
                            borderColor: "#fff",
                            borderWidth: 2,
                        }
                    ]
                });
            }

            setWeightData(weight?.data?.[0] || null);
            setSpecialHandling(special?.data?.[0] || null);
        } catch (error) {
            console.error("Error fetching package analytics:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDateRangeChange = (range) => {
        setDateRange(range);
        if (range !== "custom") {
            setShowCustomDatePicker(false);
        }
    };

    const handleCustomDateSubmit = () => {
        if (customStartDate && customEndDate) {
            if (new Date(customStartDate) > new Date(customEndDate)) {
                setError("Start date must be before end date");
                return;
            }
            setShowCustomDatePicker(false);
            setDateRange("custom");
        } else {
            setError("Please select both start and end dates");
        }
    };

    const chartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                labels: {
                    color: "#495057",
                    font: { size: 12 },
                },
            },
        },
        scales: {
            x: {
                ticks: { color: "#495057" },
                grid: { color: "#ebedef" },
            },
            y: {
                ticks: { color: "#495057" },
                grid: { color: "#ebedef" },
            },
        },
    };

    const pieOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: {
                    color: "#495057",
                    font: { size: 12 },
                    padding: 15,
                },
                position: "bottom",
            },
        },
    };

    if (loading) {
        return <div className={styles.loading}>Loading Package Analytics...</div>;
    }

    const { startDate, endDate } = getDateRange();

    return (
        <div className={styles.packageAnalytics}>
            <div className={styles.headerSection}>
                <h2 className={styles.sectionTitle}>📦 Package Analytics</h2>
                
                {/* Date Range Filter */}
                <div className={styles.filterSection}>
                    <div className={styles.dateRangeButtons}>
                        <button
                            className={`${styles.rangeButton} ${dateRange === "week" ? styles.active : ""}`}
                            onClick={() => handleDateRangeChange("week")}
                        >
                            📅 This Week
                        </button>
                        <button
                            className={`${styles.rangeButton} ${dateRange === "month" ? styles.active : ""}`}
                            onClick={() => handleDateRangeChange("month")}
                        >
                            📆 This Month
                        </button>
                        <button
                            className={`${styles.rangeButton} ${dateRange === "year" ? styles.active : ""}`}
                            onClick={() => handleDateRangeChange("year")}
                        >
                            📊 This Year
                        </button>
                        <button
                            className={`${styles.rangeButton} ${dateRange === "custom" ? styles.active : ""}`}
                            onClick={() => {
                                setDateRange("custom");
                                setShowCustomDatePicker(!showCustomDatePicker);
                            }}
                        >
                            <FiCalendar /> Custom
                        </button>
                    </div>

                    {/* Custom Date Range Picker */}
                    {showCustomDatePicker && (
                        <div className={styles.customDatePicker}>
                            <div className={styles.dateInputGroup}>
                                <div className={styles.dateInput}>
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        max={getCurrentDate()}
                                    />
                                </div>
                                <div className={styles.dateInput}>
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        max={getCurrentDate()}
                                    />
                                </div>
                            </div>
                            <div className={styles.dateActions}>
                                <button
                                    className={styles.applyButton}
                                    onClick={handleCustomDateSubmit}
                                >
                                    Apply
                                </button>
                                <button
                                    className={styles.cancelButton}
                                    onClick={() => setShowCustomDatePicker(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Date Range Display */}
                    <div className={styles.dateDisplay}>
                        📍 {startDate} to {endDate}
                    </div>
                </div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    ⚠️ {error}
                </div>
            )}

            {/* KPI Cards */}
            <div className={styles.kpiGrid}>
                <div className={styles.kpiCard}>
                    <div className={styles.kpiIcon}>
                        <FiPackage />
                    </div>
                    <div className={styles.kpiContent}>
                        <p className={styles.kpiLabel}>Total Packages</p>
                        <p className={styles.kpiValue}>
                            {monthlyData?.datasets[0]?.data?.reduce((a, b) => a + b, 0) || 0}
                        </p>
                    </div>
                </div>

                <div className={styles.kpiCard}>
                    <div className={styles.kpiIcon} style={{ color: "#f59e0b" }}>
                        <FiBarChart2 />
                    </div>
                    <div className={styles.kpiContent}>
                        <p className={styles.kpiLabel}>Total Weight</p>
                        <p className={styles.kpiValue}>
                            {weightData?.totalWeight?.toLocaleString()} lbs
                        </p>
                    </div>
                </div>

                <div className={styles.kpiCard}>
                    <div className={styles.kpiIcon} style={{ color: "#10b981" }}>
                        <FiTruck />
                    </div>
                    <div className={styles.kpiContent}>
                        <p className={styles.kpiLabel}>Avg Weight</p>
                        <p className={styles.kpiValue}>{weightData?.avgWeight?.toFixed(2)} lbs</p>
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div className={styles.chartsGrid}>
                {/* Monthly Trend */}
                <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>📈 Monthly Trend</h3>
                    {monthlyData ? (
                        <Chart type="line" data={monthlyData} options={chartOptions} />
                    ) : (
                        <p>No data available</p>
                    )}
                </div>

                {/* Status Distribution */}
                <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>📍 Package Status (by packageStatus)</h3>
                    {statusData && statusData.labels.length > 0 ? (
                        <Chart type="doughnut" data={statusData} options={pieOptions} />
                    ) : (
                        <p className={styles.noData}>No package status data available</p>
                    )}
                </div>

                {/* Delivery Method */}
                <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>🚚 Delivery Methods</h3>
                    {deliveryData ? (
                        <Chart type="bar" data={deliveryData} options={chartOptions} />
                    ) : (
                        <p>No data available</p>
                    )}
                </div>
            </div>

            {/* Weight Analytics & Special Handling */}
            <div className={styles.statsGrid}>
                {/* Weight Analytics */}
                <div className={styles.statsCard}>
                    <h3 className={styles.statsTitle}>⚖️ Weight Analytics</h3>
                    <div className={styles.statRow}>
                        <span>Total Weight</span>
                        <span className={styles.value}>{weightData?.totalWeight?.toLocaleString() || 0} lbs</span>
                    </div>
                    <div className={styles.statRow}>
                        <span>Average Weight</span>
                        <span className={styles.value}>{weightData?.avgWeight?.toFixed(2) || 0} lbs</span>
                    </div>
                    <div className={styles.statRow}>
                        <span>Maximum Weight</span>
                        <span className={styles.value}>{weightData?.maxWeight?.toFixed(2) || 0} lbs</span>
                    </div>
                    <div className={styles.statRow}>
                        <span>Minimum Weight</span>
                        <span className={styles.value}>{weightData?.minWeight?.toFixed(2) || 0} lbs</span>
                    </div>
                </div>

                {/* Special Handling */}
                <div className={styles.statsCard}>
                    <h3 className={styles.statsTitle}>🎁 Special Handling Items</h3>
                    <div className={styles.statRow}>
                        <span>Oversize Packages</span>
                        <span className={`${styles.value} ${styles.badge}`}>{specialHandling?.oversize || 0}</span>
                    </div>
                    <div className={styles.statRow}>
                        <span>Hazardous Items</span>
                        <span className={`${styles.value} ${styles.danger}`}>{specialHandling?.hazardous || 0}</span>
                    </div>
                    <div className={styles.statRow}>
                        <span>Flammable Items</span>
                        <span className={`${styles.value} ${styles.warning}`}>{specialHandling?.flammable || 0}</span>
                    </div>
                    <div className={styles.statRow}>
                        <span>Perishable Items</span>
                        <span className={`${styles.value} ${styles.info}`}>{specialHandling?.perishable || 0}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PackageAnalytics;